import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Users, Zap, Lock, ChevronDown, ChevronUp, Flame } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 🤝 Combo Ultimates Panel — dedicated UI for the synergy tier that sits
 * between two heroes. Not on either hero's Soul strand. Not in either
 * hero's CP investment tree. Its own home.
 * Canon: /app/memory/COMBO_ULTIMATE_MECHANICS_CANON_FEB2026.md
 */
export default function ComboUltimatesPanel() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [party, setParty] = useState([]);
  const [roster, setRoster] = useState([]);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    (async () => {
      try {
        const [{ data: combos }, rosterResp] = await Promise.all([
          axios.get(`${API}/combo-ultimates`),
          axios.get(`${API}/skill-tree/roster`).catch(() => ({ data: { roster: [] } })),
        ]);
        setItems(combos.items || []);
        setRoster(rosterResp?.data?.roster || []);
      } catch {
        toast.error("Couldn't load Combo Ultimates. Try again in a moment.");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  // Refresh eligibility when party changes.
  useEffect(() => {
    if (!user || party.length === 0) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/combo-ultimates?party=${encodeURIComponent(party.join(","))}`);
        setItems(data.items || []);
      } catch { /* stay silent — panel already rendered */ }
    })();
  }, [user, party]);

  const grouped = useMemo(() => {
    const byMain = {};
    for (const it of items) {
      const key = it.main_class_id || "unknown";
      (byMain[key] = byMain[key] || []).push(it);
    }
    return byMain;
  }, [items]);

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-neutral-200 flex items-center justify-center px-6">
        <div className="max-w-md w-full border border-amber-900/60 bg-[#0F0805] p-8 text-center">
          <Lock className="mx-auto mb-3 text-[#D4AF37]" size={32} />
          <h1 className="font-serif text-3xl mb-2">Combo Ultimates sealed.</h1>
          <p className="text-sm text-neutral-400 mb-6">
            Sign in to view the synergy tier — combos live between the heroes
            who share a fire, not inside their skill trees.
          </p>
          <Link to="/#auth" data-testid="combo-ultimates-signin-link" className="inline-block px-5 py-2 bg-[#D4AF37] text-black text-xs uppercase tracking-widest">
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0605] via-[#050505] to-[#0A0605] text-neutral-200">
      <header className="max-w-6xl mx-auto px-6 pt-16 pb-8 relative">
        <div className="absolute inset-0 opacity-30 pointer-events-none bg-[radial-gradient(circle_at_top,#78350f33,transparent_60%)]" />
        <div className="relative">
          <div className="flex items-center gap-3 text-[#D4AF37]/80 uppercase tracking-[0.35em] text-[10px] mb-3">
            <Flame size={12} />
            <span>Dragonious RPG · Synergy Tier</span>
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl leading-none">
            <span className="text-neutral-100">COMBO </span>
            <span className="text-[#D4AF37] drop-shadow-[0_0_18px_rgba(212,175,55,0.35)]">ULTIMATES</span>
          </h1>
          <p className="mt-4 max-w-2xl text-sm text-neutral-400 leading-relaxed">
            Two heroes. One fire. Combo Ultimates are the synergy tier —
            never either hero's personal ultimate, never on either Soul strand.
            Both must be in the active party. Trigger hero pays the mana +
            takes the 60s cooldown; their partner walks free.
          </p>
        </div>
      </header>

      <PartyPicker
        roster={roster}
        party={party}
        onChange={setParty}
      />

      <main className="max-w-6xl mx-auto px-6 pb-24 mt-8">
        {loading ? (
          <div className="flex items-center gap-3 text-neutral-500 text-sm py-16">
            <Loader2 className="animate-spin" size={16} /> Reading the synergy ledger…
          </div>
        ) : items.length === 0 ? (
          <p className="text-neutral-500 text-sm italic">No combos scribed yet.</p>
        ) : (
          <div className="space-y-10" data-testid="combo-ultimates-grid">
            {Object.entries(grouped).map(([mainId, combos]) => (
              <section key={mainId}>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3">
                  Themed to · {roster.find((r) => r.hero_id === mainId)?.nameplate || mainId}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {combos.map((c) => (
                    <ComboCard
                      key={c.combo_id}
                      combo={c}
                      expanded={expandedId === c.combo_id}
                      onToggle={() => setExpandedId(expandedId === c.combo_id ? null : c.combo_id)}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function PartyPicker({ roster, party, onChange }) {
  if (!roster?.length) return null;
  const authored = roster.filter((r) => r.authored);
  const toggle = (heroId) => {
    if (party.includes(heroId)) {
      onChange(party.filter((p) => p !== heroId));
    } else if (party.length < 4) {
      onChange([...party, heroId]);
    } else {
      toast.info("Party is full (4). Drop someone to swap in.");
    }
  };
  return (
    <div className="max-w-6xl mx-auto px-6" data-testid="combo-party-picker">
      <div className="border border-zinc-900 bg-black/40 p-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mb-3">
          <Users size={12} />
          Simulate a party — combos with both partners in-party glow gold; the rest dim
        </div>
        <div className="flex flex-wrap gap-2">
          {authored.map((r) => {
            const isSelected = party.includes(r.hero_id);
            return (
              <button
                key={r.hero_id}
                type="button"
                data-testid={`combo-party-toggle-${r.hero_id}`}
                onClick={() => toggle(r.hero_id)}
                className={`px-3 py-1 text-xs border transition-colors ${
                  isSelected
                    ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]"
                    : "border-zinc-800 text-neutral-500 hover:border-neutral-600 hover:text-neutral-300"
                }`}
              >
                {r.emblem} {r.nameplate?.split("—")[0]?.trim() || r.hero_id}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ComboCard({ combo, expanded, onToggle }) {
  const partyOk = combo.party_state === "PARTY_OK";
  const partyMissing = combo.party_state === "PARTY_MISSING";
  const isAuthored = String(combo.status || "").startsWith("✅");
  return (
    <div
      data-testid={`combo-card-${combo.combo_id}`}
      className={`border bg-[#0A0605] transition-all ${
        partyOk
          ? "border-[#D4AF37]/70 shadow-[0_0_18px_rgba(212,175,55,0.15)]"
          : partyMissing
            ? "border-zinc-900 opacity-60"
            : "border-zinc-900"
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-4 flex items-start gap-3 hover:bg-white/[0.02] transition-colors"
      >
        <span className="text-3xl leading-none mt-0.5">{combo.icon || "🤝"}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-serif text-lg text-neutral-100 leading-tight">{combo.name}</h3>
            {!isAuthored && (
              <span className="text-[9px] uppercase tracking-widest text-amber-500 bg-amber-950/40 border border-amber-900/60 px-1.5 py-0.5">
                Authoring
              </span>
            )}
          </div>
          <div className="text-[11px] text-neutral-500 mt-1">
            With <span className="text-neutral-300">{combo.partner_nameplate?.split("—")[0]?.trim() || combo.partner_hero_id}</span>
          </div>
          <div className="flex flex-wrap gap-2 mt-2 text-[10px] uppercase tracking-widest">
            <span className="border border-zinc-800 px-1.5 py-0.5 text-neutral-400">
              <Zap size={10} className="inline -mt-0.5" /> {combo.mana_cost} mana
            </span>
            <span className="border border-zinc-800 px-1.5 py-0.5 text-neutral-400">
              {combo.cooldown_sec}s CD
            </span>
            {partyOk && (
              <span className="border border-[#D4AF37]/60 text-[#D4AF37] px-1.5 py-0.5">
                Party Ready
              </span>
            )}
            {partyMissing && (
              <span className="border border-zinc-700 text-neutral-500 px-1.5 py-0.5">
                Missing partner
              </span>
            )}
          </div>
        </div>
        {expanded ? <ChevronUp size={16} className="text-neutral-500 mt-1" /> : <ChevronDown size={16} className="text-neutral-500 mt-1" />}
      </button>

      {expanded && (
        <div className="border-t border-zinc-900 p-4 space-y-4 text-sm">
          <p className="text-neutral-300 leading-relaxed">{combo.effect}</p>
          {combo.item_flavor && (
            <p className="italic text-neutral-500 text-xs">{combo.item_flavor}</p>
          )}
          {combo.mechanic?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Mechanic</div>
              <ul className="space-y-1 text-neutral-300 text-xs">
                {combo.mechanic.map((m, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-[#D4AF37]">▸</span>
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {combo.requires?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Requires</div>
              <ul className="space-y-1 text-neutral-400 text-xs">
                {combo.requires.map((r, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-neutral-600">•</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {combo.tactical_note && (
            <div className="border-l-2 border-amber-500/60 pl-3 text-amber-300/90 text-xs italic">
              {combo.tactical_note}
            </div>
          )}
          {combo.cd_note && (
            <div className="border-l-2 border-purple-500/60 pl-3 text-purple-300/80 text-xs">
              {combo.cd_note}
            </div>
          )}
          {combo.main_hero_note && (
            <p className="text-neutral-500 text-[11px] italic">{combo.main_hero_note}</p>
          )}
          {combo.lesson && (
            <p className="text-neutral-400 text-sm italic border-t border-zinc-900 pt-3">"{combo.lesson}"</p>
          )}
          {partyMissing && combo.missing_partners?.length > 0 && (
            <div className="text-xs text-neutral-500 pt-2">
              Add to party: {combo.missing_partners.join(", ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
